import { type DeepPartial } from '@modules/editor/src/utils/types';

import { type ProjectData } from '$/pages/CustomEditorPage/utils/migrations';
import { projectEndpoints } from '$/services/endpoints';
import { fetcher } from '$/services/fetcher';
import { GeneralBackendResponseObjectSchema } from '$/services/mapper/authentication';
import { validateToken } from '$/services/usecases/authentication';
import {
  GetAiHarmoniesResponseSchema,
  mapAiHarmonies,
  type RawAiHarmony,
} from '$/services/usecases/projects/mapper/aiHarmonies';
import {
  type CameraPosition,
  type InteriorStyle,
} from '$/services/usecases/projects/mapper/aiWorkflows';
import {
  CreateEditorProjectResponseSchema,
  GetEditorProjectResponseSchema,
  GetProjectFoldersResponseSchema,
  RefineEditorProjectResponseSchema,
  ReprojectEditorProjectResponseSchema,
  UploadProjectViewResponseSchema,
  type EditorProject,
} from '$/services/usecases/projects/mapper/editorProject';
import {
  GetInspirationProjectResponseSchema,
  GetLegacyInspirationProjectResponseSchema,
  UpsertInspirationProjectResponseSchema,
  type InspirationProjectLayer,
} from '$/services/usecases/projects/mapper/inspirationProjects';
import { GetProjectOverviewResponseSchema } from '$/services/usecases/projects/mapper/projectOverview';

export const uploadProjectThumbnail = async (
  projectId: string,
  image: File,
) => {
  const data = new FormData();
  data.append('file', image);

  await validateToken();

  return await fetcher(
    projectEndpoints.UPLOAD_PROJECT_THUMBNAIL_URL(projectId),
    {
      method: 'PUT',
      body: data,
      includeCredentials: true,
      parser: (data) => GeneralBackendResponseObjectSchema.parse(data),
    },
  );
};

export const addProjectViewImage = async (
  projectId: string,
  image: File,
  data: ProjectData,
  targetViewIndex: number,
) => {
  const formData = new FormData();
  formData.append('image', image);
  formData.append('data', JSON.stringify(data));
  formData.append('targetViewIndex', String(targetViewIndex));

  await validateToken();

  return await fetcher(projectEndpoints.UPLOAD_PROJECT_VIEW_URL(projectId), {
    method: 'POST',
    body: formData,
    includeCredentials: true,
    parser: (data) => UploadProjectViewResponseSchema.parse(data),
  });
};

export const getAllProjects = async () => {
  await validateToken();
  return fetcher(projectEndpoints.PROJECT_OVERVIEW_URL, {
    method: 'GET',
    includeCredentials: true,
    parser: (data) => GetProjectOverviewResponseSchema.parse(data),
  });
};

export const createEditorProject = async (
  projectName: string,
  image: File,
  isSchoolProject?: boolean,
  parentId?: string,
  schoolClassId?: string,
) => {
  await validateToken();

  const formData = new FormData();
  formData.append('image', image);
  formData.append('projectName', projectName);
  if (isSchoolProject)
    formData.append('isSchoolProject', isSchoolProject.toString());
  if (parentId) formData.append('parentId', parentId);
  if (schoolClassId) formData.append('schoolClassId', schoolClassId);

  return fetcher(projectEndpoints.SAVE_EDITOR_PROJECT_URL, {
    method: 'POST',
    includeCredentials: true,
    body: formData,
    parser: (data) => CreateEditorProjectResponseSchema.parse(data),
  });
};

export const updateEditorProject = async (
  id: string,
  updatedProject: DeepPartial<EditorProject>,
) => {
  await validateToken();

  return fetcher(projectEndpoints.GET_PROJECT_URL('editor', id), {
    method: 'PATCH',
    includeCredentials: true,
    body: {
      ...updatedProject,
    },
    parser: (data) => GetEditorProjectResponseSchema.parse(data),
  });
};

export const upsertInspirationProject = async (
  variants: InspirationProjectLayer[][],
  inspirationId: string,
  projectName: string,
  projectId?: string,
  initialHarmonyIds?: string[],
) => {
  await validateToken();
  return fetcher(projectEndpoints.UPSERT_INSPIRATION_EDITOR_PROJECT_URL, {
    method: 'PUT',
    includeCredentials: true,
    body: {
      projectId,
      projectName,
      inspirationId,
      variants,
      initialHarmonyIds,
    },
    parser: (data) => UpsertInspirationProjectResponseSchema.parse(data),
  });
};

export const getEditorProject = async (projectId: string) => {
  await validateToken();
  return fetcher(projectEndpoints.GET_PROJECT_URL('editor', projectId), {
    method: 'GET',
    includeCredentials: true,
    parser: (data) => GetEditorProjectResponseSchema.parse(data),
  });
};

export const getInspirationProject = async (projectId: string) => {
  await validateToken();
  return fetcher(projectEndpoints.GET_PROJECT_URL('inspiration', projectId), {
    method: 'GET',
    includeCredentials: true,
    parser: (data) => GetInspirationProjectResponseSchema.parse(data),
  });
};

export const getLegacyInspirationProject = async (projectId: string) => {
  await validateToken();
  return fetcher(
    projectEndpoints.GET_PROJECT_URL('legacy-inspiration', projectId),
    {
      method: 'GET',
      includeCredentials: true,
      parser: (data) => GetLegacyInspirationProjectResponseSchema.parse(data),
    },
  );
};

export const renameProject = async (projectId: string, name: string) => {
  await validateToken();
  return fetcher(projectEndpoints.RENAME_PROJECT_URL(projectId), {
    method: 'PATCH',
    includeCredentials: true,
    body: {
      name,
    },
    parser: (data) => GeneralBackendResponseObjectSchema.parse(data),
  });
};

export const duplicateProject = async (projectId: string, name: string) => {
  await validateToken();
  return fetcher(projectEndpoints.DUPLICATE_PROJECT_URL(projectId), {
    method: 'POST',
    includeCredentials: true,
    body: {
      name,
    },
    parser: (data) => GeneralBackendResponseObjectSchema.parse(data),
  });
};

export const deleteProject = async (projectId: string) => {
  await validateToken();
  return fetcher(projectEndpoints.DELETE_PROJECT_URL(projectId), {
    method: 'DELETE',
    includeCredentials: true,
    parser: (data) => GeneralBackendResponseObjectSchema.parse(data),
  });
};

export const changeProjectVisibility = async (
  projectId: string,
  isPublic: boolean,
) => {
  await validateToken();

  return await fetcher(
    projectEndpoints.CHANGE_PROJECT_VISIBILITY_URL(projectId),
    {
      body: { isPublic },
      method: 'PATCH',
      parser: (data) => GeneralBackendResponseObjectSchema.parse(data),
      includeCredentials: true,
    },
  );
};

export const getProjectFolders = async () => {
  await validateToken();
  return fetcher(projectEndpoints.PROJECT_FOLDERS_URL, {
    method: 'GET',
    includeCredentials: true,
    parser: (data) => GetProjectFoldersResponseSchema.parse(data),
  });
};

export const createProjectFolder = async (folderName: string) => {
  await validateToken();
  return fetcher(projectEndpoints.PROJECT_FOLDERS_URL, {
    method: 'POST',
    includeCredentials: true,
    body: {
      name: folderName,
    },
    parser: (data) => GeneralBackendResponseObjectSchema.parse(data),
  });
};

export const renameProjectFolder = async (
  folderId: string,
  folderName: string,
) => {
  await validateToken();
  return fetcher(`${projectEndpoints.PROJECT_FOLDERS_URL}/${folderId}/name`, {
    method: 'PATCH',
    includeCredentials: true,
    body: {
      newName: folderName,
    },
    parser: (data) => GeneralBackendResponseObjectSchema.parse(data),
  });
};

export const deleteProjectFolder = async (folderId: string) => {
  await validateToken();
  return fetcher(`${projectEndpoints.PROJECT_FOLDERS_URL}/${folderId}`, {
    method: 'DELETE',
    includeCredentials: true,
    parser: (data) => GeneralBackendResponseObjectSchema.parse(data),
  });
};

export const refineEditorProject = async (
  projectId: string,
  prompt: string,
  imageSrc: string,
) => {
  await validateToken();
  return fetcher(projectEndpoints.REFINE_EDITOR_PROJECT_URL(projectId), {
    method: 'POST',
    includeCredentials: true,
    body: {
      prompt,
      imageSrc,
    },
    parser: (data) => RefineEditorProjectResponseSchema.parse(data),
  });
};

export const reprojectEditorProject = async (
  projectId: string,
  perspective: string,
  imageSrc: string,
) => {
  await validateToken();
  return fetcher(projectEndpoints.REPROJECT_EDITOR_PROJECT_URL(projectId), {
    method: 'POST',
    includeCredentials: true,
    body: {
      perspective,
      imageSrc,
    },
    parser: (data) => ReprojectEditorProjectResponseSchema.parse(data),
  });
};

export const correctEditorProjectWithAi = async (
  projectId: string,
  viewIndex = 0,
) => {
  await validateToken();
  return fetcher(
    projectEndpoints.AI_CORRECTION_EDITOR_PROJECT_URL(projectId, viewIndex),
    {
      method: 'POST',
      includeCredentials: true,
      body: {},
      parser: (data) => GetEditorProjectResponseSchema.parse(data),
    },
  );
};

export const reprojectEditorProjectWithAi = async (
  projectId: string,
  cameraPosition: CameraPosition,
  viewIndex = 0,
) => {
  await validateToken();
  return fetcher(
    projectEndpoints.AI_REPROJECT_EDITOR_PROJECT_URL(
      projectId,
      cameraPosition,
      viewIndex,
    ),
    {
      method: 'POST',
      includeCredentials: true,
      body: {},
      parser: (data) => GetEditorProjectResponseSchema.parse(data),
    },
  );
};

export const restyleEditorProjectWithAi = async (
  projectId: string,
  style: InteriorStyle,
  viewIndex = 0,
) => {
  await validateToken();
  return fetcher(
    projectEndpoints.AI_INTERIOR_STYLE_EDITOR_PROJECT_URL(
      projectId,
      style,
      viewIndex,
    ),
    {
      method: 'POST',
      includeCredentials: true,
      body: {},
      parser: (data) => GetEditorProjectResponseSchema.parse(data),
    },
  );
};

export const editEditorProjectWithAiAssistant = async (
  projectId: string,
  userInput: string,
  viewIndex = 0,
) => {
  await validateToken();
  return fetcher(
    projectEndpoints.AI_ASSISTANT_EDITOR_PROJECT_URL(projectId, viewIndex),
    {
      method: 'POST',
      includeCredentials: true,
      body: { userInput },
      parser: (data) => GetEditorProjectResponseSchema.parse(data),
    },
  );
};

export const segmentEditorProjectWithAi = async (
  projectId: string,
  viewIndex = 0,
) => {
  await validateToken();
  return fetcher(
    projectEndpoints.AI_SEGMENTATION_EDITOR_PROJECT_URL(projectId, viewIndex),
    {
      method: 'POST',
      includeCredentials: true,
      body: {},
      parser: (data) => GetEditorProjectResponseSchema.parse(data),
    },
  );
};

export const getEditorProjectHarmoniesWithAi = async (
  projectId: string,
  viewIndex = 0,
) => {
  await validateToken();
  return fetcher(
    projectEndpoints.AI_HARMONIES_EDITOR_PROJECT_URL(projectId, viewIndex),
    {
      method: 'POST',
      includeCredentials: true,
      body: {},
      parser: (data) => {
        const parsed = GetAiHarmoniesResponseSchema.parse(data);

        return {
          ...parsed,
          payload: parsed.payload ? mapAiHarmonies(parsed.payload) : undefined,
        };
      },
    },
  );
};

export const recolorEditorProjectWithAiHarmony = async (
  projectId: string,
  selectedHarmony: RawAiHarmony,
  viewIndex = 0,
) => {
  await validateToken();
  return fetcher(
    projectEndpoints.AI_HARMONIES_RECOLOR_EDITOR_PROJECT_URL(
      projectId,
      viewIndex,
    ),
    {
      method: 'POST',
      includeCredentials: true,
      body: { selectedHarmony },
      parser: (data) => GetEditorProjectResponseSchema.parse(data),
    },
  );
};
